import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Account,
  AccountId,
  HowToSaveType,
  Target,
} from 'components/features/target-achievement-support/utils/type';
import { useTargetSupportRouting } from 'components/features/target-achievement-support/hooks/useTargetSupportRouting';
import {
  howToSaveTypeToImg,
  howToSaveTypeToTitle,
} from 'components/features/target-achievement-support/utils/targetInfo';
import {
  commaFormat,
  yieldFormatPercentage,
} from 'components/features/new-report/utils/formatter';
import { calculateTargetDate } from 'components/features/target-achievement-support/utils/birthday';
import { isHowToSaveInvestmentTrust } from '../TargetImformation';
import { getUserBroker } from 'config/api';

const getInitialAmount = (
  accs: Account[],
  selectedAccountIds: AccountId[]
): number =>
  accs
    .filter(
      account =>
        selectedAccountIds.includes(account.account) && account.available
    )
    .reduce((acc, account) => acc + account.totalAssetAmount, 0);

export const HowToSaveCard = ({
  target,
  targetHowToSaveType,
  birthday,
  goTargetUpdateConfirm,
  className,
  registeredAccounts,
}: {
  target: Target;
  targetHowToSaveType: HowToSaveType;
  birthday: Date;
  className?: string;
  goTargetUpdateConfirm: () => void;
  registeredAccounts: string[];
}): React.ReactElement => {
  const { goWhatIsNecessaryYield } = useTargetSupportRouting();

  const { data: accounts } = useQuery(
    'howToSaveCardUserBroker',
    () => getUserBrokerAPI(),
    {
      refetchOnWindowFocus: false,
    }
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const title = howToSaveTypeToTitle(targetHowToSaveType);
  const registeredAccountCount = target.accountList.length;
  const registeredAccountIds = target.accountList.map(list => list.account);
  const { age, month, year } = calculateTargetDate({
    birthday: birthday,
    targetDate: new Date(target.endDate),
  });

  if (accounts === null || accounts === undefined) return <></>;

  const initialAmount = getInitialAmount(accounts, registeredAccountIds);

  return (
    <>
      <div className={`bg-white border rounded-lg ${className ?? ''}`}>
        <div
          className="grid pl-4 border-b cursor-pointer"
          style={{ gridTemplateColumns: '1fr auto' }}
          onClick={(): void => setIsOpen(prev => !prev)}>
          <div className="flex flex-col pt-4 pb-4">
            <div className="flex flex-row items-center">
              <img
                src={howToSaveTypeToImg(targetHowToSaveType)}
                alt="目標アイコン"
                className="rounded-full w-15 h-15 border"
                style={{ borderColor: '#dee5ee', objectFit: 'cover' }}
              />
              <div className="flex flex-col ml-3">
                <div className="text-sm">貯め方</div>
                <div className="text-lg font-bold">{title}</div>
                <div className="text-sm">
                  設定中の口座　{registeredAccountCount}件
                </div>
              </div>
            </div>
            {isHowToSaveInvestmentTrust(targetHowToSaveType) && (
              <div className="flex items-center mt-1">
                <div className="text-xs">目標達成に必要な年利回り</div>
                <div className="text-base font-bold ml-6">
                  {yieldFormatPercentage(target.yield, {
                    numberOfDecimals: 1,
                  })}
                  <span className="text-xs font-bold">%</span>
                </div>
              </div>
            )}
          </div>
          <div
            className={`icon-chevron-up ${
              isOpen ? 'transform rotate-180' : 'transform rotate-0'
            } transition duration-300 text-bluegray-400 font-bold mx-4 flex items-center`}></div>
        </div>
        <div
          className={`transition-all duration-300 ${
            isOpen
              ? 'ui-effect-fade-in-300 visible'
              : 'ui-effect-fade-out-300 invisible'
          }`}
          style={{
            maxHeight: `${isOpen ? 350 : 0}px`,
          }}>
          <div className="flex items-center px-4 py-2 text-xs font-bold border-b">
            <div className="text-bluegray-500">目標金額</div>
            <div className="ml-auto text-bluegray-700">
              <span className="text-base mr-1">
                {commaFormat(target.targetAmount, {
                  numberOfDecimals: 0,
                })}
              </span>
              円
            </div>
          </div>
          <div className="flex items-center px-4 py-2 text-xs font-bold border-b">
            <div className="text-bluegray-500">達成時期</div>
            <div className="ml-auto text-base text-bluegray-700">
              {year}年{month}月({age}歳)
            </div>
          </div>
          <div className="flex items-center px-4 py-2 text-xs font-bold border-b">
            <div className="text-bluegray-500">毎月の積立額</div>
            <div className="ml-auto text-bluegray-700">
              <span className="text-base mr-1">
                {commaFormat(target.reserveAmount, {
                  numberOfDecimals: 0,
                })}
              </span>
              円
            </div>
          </div>
          <div>
            <div className="flex items-center mx-4 py-2 text-xs font-bold border-b border-dotted">
              <div className="text-bluegray-500">初期投資金額</div>
              <div className="ml-auto text-bluegray-700">
                <span className="text-base mr-1">
                  {commaFormat(initialAmount, {
                    numberOfDecimals: 0,
                  })}
                </span>
                円
              </div>
            </div>
            <div className="flex mx-4 py-3 border-b">
              <div className="w-1/3 text-xs px-2 mt-1 text-bluegray-500">
                登録口座
              </div>
              <div className="w-2/3 flex flex-wrap justify-end">
                {registeredAccounts.length !== 0 ? (
                  registeredAccounts.map((account, key) => {
                    return (
                      <div key={key}>
                        {account !== null && (
                          <div
                            className="border border-bluegray-300 rounded text-xs px-1 ml-1 mt-1"
                            style={{
                              backgroundColor: '#f3f4f6',
                              color: '#3f3f46',
                            }}>
                            {account}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <>
                    <div className="ml-auto mt-1 text-sm font-bold">なし</div>
                  </>
                )}
              </div>
            </div>
            <div className="flex py-2 border-b">
              <div className="pl-6 text-xs text-bluegray-500">うち除外金額</div>
              <div className="pr-4 ml-auto text-sm text-bluegray-700 font-bold">
                <span className="mr-1">
                  {commaFormat(target.excludeAmount, {
                    numberOfDecimals: 0,
                  })}
                </span>
                円
              </div>
            </div>
          </div>
          <div
            className="px-4 py-3 text-sm text-blue-700 text-right cursor-pointer"
            onClick={goTargetUpdateConfirm}>
            <div>設定変更</div>
          </div>
        </div>
      </div>
      {isHowToSaveInvestmentTrust(targetHowToSaveType) && (
        <div
          className="mt-1 mr-4 flex items-center justify-end text-bluegray-500 cursor-pointer"
          onClick={() => goWhatIsNecessaryYield(target.id)}>
          <div className="text-sm pr-2">必要な年利回りとは</div>
          <div className="icon-help_outline"></div>
        </div>
      )}
    </>
  );
};

const getUserBrokerAPI = async () => {
  const ret = await getUserBroker();
  if (ret === null || ret.Items === null) return null;

  return ret.Items.reduce((acc, v) => {
    return [
      ...acc,
      (item => {
        return {
          totalAssetAmount: item.TotalAssetAmount,
          account: item.Account,
          available: item.Available,
          brokerName: item.BrokerName,
          aggregateSource: item.AggregateSource,
          brokerType: item.BrokerType,
        };
      })(v),
    ];
  }, [] as Account[]);
};
